import {
  FrequencyUnit,
  Task,
  TaskCheckOptions,
  Timestamp,
} from '@griply/types';
import {
  getScheduleByDate,
  getScheduleFrequency,
  getScheduleFrequencyDayTarget,
} from '@griply/utils';
import { addDays, format, isAfter, isBefore, isSameDay } from 'date-fns';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useToday } from 'shared/contexts/today';
import { formatTimeRange } from 'shared/utils/format-time-range';
import { getTaskCheckOption } from 'shared/utils/get-task-check-option';
import { getTaskDayCompletionPercentage } from 'shared/utils/get-task-day-completion-percentage';

import { useData } from '../../use-data';
import { DayEntry } from '../upcoming/types';

export type UseSlotDataOptions = {
  task: Task;
  date: Timestamp;
  entry?: DayEntry;
};

export const useSlotData = ({ task, date, entry }: UseSlotDataOptions) => {
  const { t } = useTranslation();
  const today = useToday();
  const { weekStartsOn, timeFormat } = useData();

  const activeSchedule = useMemo(
    () => getScheduleByDate(task.schedules, { date, today }),
    [task.schedules, date, today],
  );

  const isToday = useMemo(() => isSameDay(date, today), [date, today]);

  const label = useMemo(() => {
    if (isToday) {
      return t('general.labels.dates.today');
    }
    if (isSameDay(date, addDays(today, 1))) {
      return t('general.labels.dates.tomorrow');
    }

    const frequency = activeSchedule
      ? getScheduleFrequency(activeSchedule)
      : undefined;

    if (frequency === FrequencyUnit.Month) {
      return format(date, 'EEEE, d MMM');
    }

    return format(date, 'EEEE');
  }, [date, isToday, t, today, activeSchedule]);

  const checked = useMemo(
    () =>
      getTaskCheckOption(task, {
        day: date,
        today,
        weekStartsOn,
        showRepeatingAsTodo: true,
      }),
    [date, task, today, weekStartsOn],
  );

  const completionPercentage = useMemo(
    () =>
      task?.schedules?.length
        ? getTaskDayCompletionPercentage(task, {
            day: date,
            weekStartsOn,
          })
        : undefined,
    [date, task, weekStartsOn],
  );

  const isFuture = useMemo(() => isAfter(date, today), [date, today]);

  const overdue = useMemo(
    () =>
      !!entry &&
      checked === TaskCheckOptions.Unchecked &&
      isBefore(date, today) &&
      !isSameDay(date, today),
    [checked, date, entry, today],
  );

  const target = useMemo(() => {
    if (!activeSchedule) {
      return;
    }

    return activeSchedule.frequency
      ? getScheduleFrequencyDayTarget(activeSchedule)
      : activeSchedule.completionTarget.count;
  }, [activeSchedule]);

  const timeLabel = useMemo(() => {
    const value = entry?.timeslot;
    if (!value || value.startTime === undefined || value.startTime === null) {
      return t('general.labels.all-day');
    }

    const startDate = new Date();
    startDate.setHours(0, 0, 0, value.startTime);

    if (!value.duration) {
      return format(startDate, timeFormat);
    }

    const endDate = new Date();
    endDate.setHours(0, 0, 0, value.startTime + value.duration);

    return formatTimeRange(startDate, endDate, timeFormat);
  }, [t, timeFormat, entry?.timeslot]);

  return {
    isToday,
    label,
    checked,
    completionPercentage,
    isFuture,
    overdue,
    target,
    timeLabel,
  };
};
